import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import axiosInstance from '@/config/axios';
import { toast } from 'sonner';

const MAX_COMPARE = 3;
const LOCAL_STORAGE_KEY = 'compareList';

export function useComparison() {
  const { user } = useAuth();
  const [compareIds, setCompareIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchComparisons = useCallback(async () => {
    if (user) {
      try {
        const response = await axiosInstance.get('/api/comparisons');
        setCompareIds(response.data.propertyIds || []);
      } catch (error) {
        console.error('Error fetching remote comparisons:', error);
      }
    } else {
      const local = localStorage.getItem(LOCAL_STORAGE_KEY);
      setCompareIds(local ? JSON.parse(local) : []);
    }
    setIsLoading(false);
  }, [user]);

  useEffect(() => {
    fetchComparisons();
  }, [fetchComparisons]);

  const addToCompare = async (propertyId: string) => {
    if (compareIds.includes(propertyId)) return;
    
    if (compareIds.length >= MAX_COMPARE) {
      toast.error(`Máximo ${MAX_COMPARE} propiedades para comparar`);
      return;
    }

    const newIds = [...compareIds, propertyId];
    
    if (user) {
      try {
        await axiosInstance.post('/api/comparisons', { propertyId });
        setCompareIds(newIds);
        toast.success('Agregado a comparación');
      } catch (error) {
        toast.error('Error al sincronizar comparación');
      }
    } else {
      setCompareIds(newIds);
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(newIds));
      toast.success('Agregado a comparación (Local)');
    }
  };

  const removeFromCompare = async (propertyId: string) => {
    const newIds = compareIds.filter(id => id !== propertyId);
    
    if (user) {
      try {
        await axiosInstance.delete(`/api/comparisons/${propertyId}`);
        setCompareIds(newIds);
      } catch (error) {
        toast.error('Error al eliminar de comparación');
      }
    } else {
      setCompareIds(newIds);
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(newIds));
    }
  };

  const clearCompare = async () => {
    if (user) {
      // In a real app we'd have a clear endpoint or loop deletions
      for (const id of compareIds) {
        await axiosInstance.delete(`/api/comparisons/${id}`);
      }
    }
    setCompareIds([]);
    localStorage.removeItem(LOCAL_STORAGE_KEY);
  };

  return {
    compareIds,
    addToCompare,
    removeFromCompare,
    clearCompare,
    isLoading,
    isMaxReached: compareIds.length >= MAX_COMPARE
  };
}
